import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Typography,
  Input,
} from "@altimateai/lego";
import classNames from "classnames";
import {
  getUserList,
  addNewUser,
  updateUser,
  updateUserRole,
  deleteUser,
} from "../../helpers/apis";
import styles from "./styles.module.scss";
import vEllipsis from "../../assets/icons/vertical_ellipsis.svg";
import { isAdmin, isOwner } from "../../services/auth";
import { useAppState } from "@/modules/app/useAppContext";
import { getTenant } from "../../helpers/tenant";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { capitalize } from "lodash";
import { BetterPopover } from "../../components/popover";
import { GET_USER_LIST } from "../../services/constants";
import { PermissionsEnum, RolesEnum } from "../../helpers/constants";
import MailChecker from "mailchecker";
import { default as TickSquareIcon } from "../../assets/icons/tick_square.svg?react";
import { default as InfoSquareIcon } from "../../assets/icons/info_square.svg?react";
import ApiSuccess from "../../assets/icons/datastore_form_success.svg";
import { freeEmailDomains } from "../authentication/freeEmailDomains";
import { DataTable } from "../../components/tables";
import { RelativeComponentLoader } from "../../components/loader";
import { SuccessCircleCheck } from "@/assets/icons";
import RolesDropdown from "./components/rolesDropdown/RolesDropdown";
import { Stack, Popconfirm, toast } from "@altimateai/lego";
import { downloadAltimateUsersCSV } from "@/helpers/apis";
import { Paginate } from "@/components/paginate";
import { useDebounce } from "../../helpers/hooks";
import type { z } from "zod";
import { BaseUserSchema } from "@/types";
import { TableHeaderType } from "@/components/tables/types";
import { formatUserName } from "@/helpers/utils";
import dayjs from "dayjs";
import { useResolveRoleConflict } from "./sso-group-sync/hooks";
import { RoleConflictDialog } from "./components/RoleConflictDialog";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { SettingsTracking } from "@/lib/tracking/settings";

const EMAIL_INPUT_ID = "free-user-email-input";

type UserListItem = z.infer<typeof BaseUserSchema>;

const getUserStatus = (user: UserListItem) => {
  if (user.is_active) return "active";
  if (user.is_verified) return "inactive";
  if (user?.attributes && user.attributes.deactivated === true) return "Deactivated";
  if (user.is_invited) return "invited";
  if (user?.attributes && user.is_onboarded === false) return "Not Signed Up";
  return "";
};

const styleMap: Record<string, string> = {
  active: styles.status_active,
  inactive: styles.status_inactive,
  invited: styles.status_invited,
  "Not Signed Up": styles.status_inactive,
  Deactivated: styles.status_inactive,
};

const UserList = () => {
  const { state } = useLocation();
  const [showApiSuccess, setShowApiSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [showModal, setShowModal] = useState(state?.invite || false);
  const [showReInviteSuccess, setShowReInviteSuccess] = useState(false);
  // Removed showDeleteConfirmation and userToDelete state - using Popconfirm directly
  const [email, setEmail] = useState("");
  const [inviteUserRole, setInviteUserRole] = useState("");
  const [usersDownloadType, _] = useState("csv");
  const { loggedinUser: user } = useAppState();
  const tenant = getTenant();
  let { company } = useParams();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [page, setPage] = useState(Number(searchParameters.get("page")) || 0);
  const [pageSize, setPageSize] = useState(Number(searchParameters.get("size")) || 20);
  const [searchTerm, setSearchTerm] = useState("");
  const [conflictUser, setConflictUser] = useState<UserListItem | null>(null);
  const sortColumn = searchParameters.get("sortColumn") || "created_at";
  const sortOrder = (searchParameters.get("sortOrder") || "desc") as "asc" | "desc" | "";
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useBreadcrumb("Users", {
    defaultParents: [{ label: "Settings", pathname: "/settings", noLink: true }],
  });

  if (tenant) {
    company = tenant;
  }

  const resolveConflict = useResolveRoleConflict();

  const handleOpenConflictDialog = useCallback((item: UserListItem) => {
    if (item.role_title && item.sso_resolved_role_title) {
      setConflictUser(item);
    }
  }, []);

  const updateRole = useMutation({
    mutationFn: (data: { user_uuid: string; role_title: string }) => {
      return updateUserRole(data);
    },
    onSuccess: (data, variables) => {
      if (data?.ok) {
        SettingsTracking.userRoleChanged(variables.user_uuid, variables.role_title);
        queryClient.invalidateQueries({ queryKey: [GET_USER_LIST] });
      }
    },
  });

  const queryClient = useQueryClient();
  const { data: usersResponse } = useQuery({
    queryKey: [GET_USER_LIST, page, pageSize, debouncedSearchTerm, sortColumn, sortOrder],
    queryFn: () =>
      getUserList({
        company: company || "",
        with_service_user: false,
        allUsers: false,
        page,
        pageSize,
        search_term: debouncedSearchTerm,
        include_deleted: false,
        sortColumn,
        sortOrder,
      }),
  });
  const isPublicTenant = tenant === "";
  const changeRolePermission = isPublicTenant ? false : isOwner() || isAdmin();
  useEffect(() => {
    if (isPublicTenant) {
      setInviteUserRole(RolesEnum.OWNER);
    } else if (!changeRolePermission) {
      if (user?.role_FK?.role_title) {
        setInviteUserRole(user.role_FK.role_title);
      } else {
        console.error("User role_FK.role_title is undefined");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { mutate: _inviteUser } = useMutation({
    mutationFn: () => addNewUser({ email, company: company || "", role: inviteUserRole }),
    onSuccess: () => {
      SettingsTracking.userInvited(inviteUserRole);
      setShowApiSuccess(true);
      setTimeout(() => {
        setShowModal(false);
        setShowApiSuccess(false);
        setEmail("");
        setInviteUserRole("");
        setEmailError("");
        if (isPublicTenant) {
          setInviteUserRole(RolesEnum.OWNER);
        } else if (!changeRolePermission) {
          if (user?.role_FK?.role_title) {
            setInviteUserRole(user.role_FK.role_title);
          } else {
            console.error("User role_FK.role_title is undefined");
          }
        }
        queryClient.invalidateQueries({ queryKey: [GET_USER_LIST] });
      }, 3000);
    },
  });
  const { mutate: _updateUser } = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_USER_LIST] });
    },
  });
  const { refetch: downloadAllUsers } = useQuery({
    queryKey: ["downloadAllUsers"],
    queryFn: () =>
      downloadAltimateUsersCSV({
        company: company || "",
        withServiceUser: false,
        allUsers: false,
        type: usersDownloadType,
      }),
    enabled: false,
  });
  const { mutate: _reInviteUser } = useMutation({
    mutationFn: addNewUser,
    onSuccess: () => {
      setShowReInviteSuccess(true);
      setTimeout(() => {
        setShowReInviteSuccess(false);
      }, 3000);
    },
  });

  const { mutate: _deleteUser } = useMutation({
    mutationFn: ({ uuid }: { uuid: string }) => deleteUser({ uuid, company: company || "" }),
    onSuccess: (_data, variables) => {
      SettingsTracking.userDeleted(variables.uuid);
      queryClient.invalidateQueries({ queryKey: [GET_USER_LIST] });
    },
    onError: error => {
      toast({
        variant: "destructive",
        title: "Failed to delete user",
        description: error instanceof Error ? error.message : String(error),
      });
    },
  });

  const handleReInvite = (user: { email: string; role_title?: string | null }) => {
    SettingsTracking.userReinvited(user.email);
    _reInviteUser({
      email: user.email,
      company: company || "",
      role: user.role_title ?? RolesEnum.USER,
    });
  };

  const handleDeleteUser = (user: UserListItem) => {
    _deleteUser({ uuid: user.uuid });
  };

  function isFreeEmailProvider(email: string): boolean {
    if (email.includes("@")) {
      const domain = email.split("@")[1];
      return freeEmailDomains.includes(domain);
    }
    return false;
  }

  const validateEmail = (e: string) => {
    if (!e) {
      setEmailError("");
    } else if (isFreeEmailProvider(e)) {
      setEmailError("Please use a valid business email address");
    } else if (!MailChecker.isValid(e)) {
      setEmailError("Please use a valid email address");
    } else {
      setEmailError("");
    }
  };

  useEffect(() => {
    setSearchParameters(params => {
      params.set("page", page.toString());
      params.set("size", pageSize.toString());
      return params;
    });
  }, [page, pageSize, setSearchParameters]);

  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    // Reset page to 0 when search term or sort changes
    setPage(0);
  }, [searchTerm, sortColumn, sortOrder]);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const handleDownloadAllUsers = async () => {
    await downloadAllUsers();
  };

  const submitDisabled = !email || emailError !== "" || !inviteUserRole;
  const header: TableHeaderType<UserListItem>[] = [
    {
      id: 1,
      label: "Name",
      uid: "name",
      width: "18%",
      sort: {
        sortSearchKey: "sortColumn",
        sortOrderSearchKey: "sortOrder",
        sortParamValue: "first_name",
      },
      render: item => <div className="al-break-all">{formatUserName(item)}</div>,
    },
    {
      id: 2,
      label: "Email",
      uid: "email",
      width: "25%",
      render: item => <div className="al-break-all">{item.email}</div>,
    },
    {
      id: 3,
      label: "Status",
      uid: "status",
      width: "8%",
      sort: {
        sortSearchKey: "sortColumn",
        sortOrderSearchKey: "sortOrder",
        sortParamValue: "is_active",
      },
      render: item => {
        const status = getUserStatus(item);
        return <div className={styleMap[status]}>{capitalize(status)}</div>;
      },
    },
    {
      id: 4,
      label: "Created at (UTC)",
      uid: "created_at",
      width: "11%",
      sort: {
        sortSearchKey: "sortColumn",
        sortOrderSearchKey: "sortOrder",
        sortParamValue: "created_at",
      },
      render: item => <span>{dayjs.utc(item.created_at).format("DD MMM YYYY, hh:mm A")}</span>,
    },
    {
      id: 5,
      label: "Last Login (UTC)",
      uid: "last_login_at",
      width: "11%",
      sort: {
        sortSearchKey: "sortColumn",
        sortOrderSearchKey: "sortOrder",
        sortParamValue: "last_login_at",
      },
      render: item => (
        <span>
          {item.last_login_at
            ? dayjs.utc(item.last_login_at).format("DD MMM YYYY, hh:mm A")
            : "Never"}
        </span>
      ),
    },
    {
      id: 6,
      label: "Role",
      uid: "role",
      width: "10%",
      render: item => (
        <RolesDropdown
          id={"role-option-" + item.uuid}
          userRole={item.role_title ?? ""}
          setUserRole={role_title => {
            updateRole.mutate({
              user_uuid: item.uuid,
              role_title: role_title,
            });
          }}
          dispDropdown={!isPublicTenant && changeRolePermission && user?.uuid !== item.uuid}
          roleSource={item.role_source}
          hasRoleConflict={item.has_role_conflict}
          ssoResolvedRoleTitle={item.sso_resolved_role_title}
          onResolveConflict={() => handleOpenConflictDialog(item)}
        />
      ),
    },
    {
      id: 7,
      label: "Actions",
      uid: "actions",
      width: "12%",
      render: item => {
        const status = getUserStatus(item);
        const actionId = "option-" + item.uuid;
        const canManageUser =
          !isPublicTenant && user?.uuid !== item.uuid && (isOwner() || isAdmin());
        const canDeactivate = status === "active" && canManageUser;
        const canReInvite =
          status === "invited" &&
          (isPublicTenant ||
            (user?.uuid !== item.uuid && user?.permission?.includes(PermissionsEnum.USER_ADD)));
        const canDelete =
          !isPublicTenant &&
          user?.uuid !== item.uuid &&
          Boolean(user?.permission?.includes(PermissionsEnum.USER_DELETE));

        return (
          <BetterPopover
            id={`popover-${actionId}`}
            trigger={
              <Button variant="ghost" className={styles.action} aria-label="Options">
                <img src={vEllipsis} alt="Options" />
              </Button>
            }
            render={({ close }) => (
              <ul className={styles.list}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => {
                    e.preventDefault();
                    SettingsTracking.userDeactivated(item.uuid);
                    _updateUser({
                      _id: item.uuid,
                      is_active: false,
                      is_verified: false,
                      attributes: { deactivated: true },
                    });
                    close();
                  }}
                  disabled={!canDeactivate}
                >
                  Deactivate
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => {
                    e.preventDefault();
                    handleReInvite(item);
                  }}
                  disabled={!canReInvite}
                >
                  Reinvite
                </Button>
                <Popconfirm
                  title="Delete User"
                  description={`Are you sure you want to delete "${item.first_name}${item.last_name ? ` ${item.last_name}` : ""}" (${item.email})? This action cannot be undone.`}
                  onConfirm={e => {
                    e?.stopPropagation();
                    handleDeleteUser(item);
                    close();
                  }}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button variant="ghost" size="sm" disabled={!canDelete}>
                    Delete
                  </Button>
                </Popconfirm>
                {showReInviteSuccess && (
                  <Stack direction="row" className="al-pl-2 al-gap-2 al-items-center">
                    <SuccessCircleCheck />
                    <p>Invite sent</p>
                  </Stack>
                )}
              </ul>
            )}
          />
        );
      },
    },
  ];

  return (
    <>
      <Card>
        <CardContent className="p-4">
          <Stack direction="row" className="al-justify-between al-items-center al-mb-4">
            <Typography variant="page-heading">Users</Typography>
            <Stack direction={"row"} align={"center"} gap={"md"}>
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="al-w-64"
              />
              <Button onClick={() => handleDownloadAllUsers()}>Download</Button>
              <Button
                onClick={e => {
                  e.preventDefault();
                  setShowModal(true);
                }}
                disabled={!isPublicTenant && !user?.permission?.includes(PermissionsEnum.USER_ADD)}
              >
                Invite user
              </Button>
            </Stack>
          </Stack>
          {usersResponse?.users ? (
            <>
              <DataTable<UserListItem>
                header={header}
                data={{ items: usersResponse.users }}
                idKey={item => item.uuid}
                defaultSort={{ sortParamValue: "created_at", order: "desc" }}
              />
              <Paginate
                showPageSize
                itemCount={usersResponse?.meta?.count}
                page={page}
                numPages={usersResponse?.meta?.total_pages}
                onPageClick={handlePageChange}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          ) : (
            <RelativeComponentLoader label="Loading users..." />
          )}
        </CardContent>
      </Card>
      <Dialog open={showModal} onOpenChange={(open: boolean) => setShowModal(open)}>
        <DialogContent>
          <h5 className="al-font-bold">Invite User</h5>
          <p className="text-muted">An invitation email will be sent to the user</p>
          <div className={styles.role_input}>
            <Stack direction="column" gap="none">
              <div
                className={classNames(styles.tenant_form_input, {
                  [styles.in_focus]: document.activeElement?.id === EMAIL_INPUT_ID,
                  [styles.is_invalid]: emailError !== "",
                })}
              >
                <Input
                  id={EMAIL_INPUT_ID}
                  name="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(e.target.value);
                    validateEmail(e.target.value);
                  }}
                />
                {email !== "" && (
                  <div className={styles.icon}>
                    {emailError === "" ? <TickSquareIcon /> : <InfoSquareIcon />}
                  </div>
                )}
              </div>
              {emailError !== "" && (
                <div
                  className={classNames(styles.form_feedback, {
                    [styles.is_error]: emailError !== "",
                  })}
                >
                  <span>{emailError}</span>
                </div>
              )}
            </Stack>
            <div></div>
            <RolesDropdown
              id={"send-invite-" + user?.uuid}
              userRole={inviteUserRole}
              setUserRole={setInviteUserRole}
              dispDropdown={!isPublicTenant && changeRolePermission}
            />
          </div>
          <Button
            className={classNames("mt-4", styles.send_invite, {
              [styles.send_invite_disabled]: submitDisabled,
            })}
            disabled={submitDisabled}
            onClick={() => _inviteUser()}
          >
            Send Invite
          </Button>
          {showApiSuccess && (
            <Stack direction="row" className="al-gap-2 al-mt-3">
              <img src={ApiSuccess} alt="Success" />
              <span className={styles.api_success}>Invitation sent successfully</span>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
      <RoleConflictDialog
        user={conflictUser}
        onOpenChange={open => !open && setConflictUser(null)}
        onResolve={async action => {
          if (!conflictUser) return;
          try {
            await resolveConflict.mutateAsync({
              user_uuid: conflictUser.uuid,
              action,
            });
            setConflictUser(null);
            toast({
              title: action === "accept_sso" ? "SSO role applied" : "Conflict dismissed",
            });
          } catch (error) {
            toast({
              variant: "destructive",
              title: "Failed to resolve conflict",
              description: String(error),
            });
          }
        }}
        isPending={resolveConflict.isPending}
      />
    </>
  );
};

export { UserList };
